import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CollectionComparison = ({ data }) => {
  const [sortBy, setSortBy] = useState('roi');
  const [sortOrder, setSortOrder] = useState('desc');

  const sortedData = [...(data || [])]?.sort((a, b) => {
    const multiplier = sortOrder === 'desc' ? -1 : 1;
    
    switch (sortBy) {
      case 'roi':
        return multiplier * (b?.roi - a?.roi);
      case 'value':
        return multiplier * (b?.totalValue - a?.totalValue);
      case 'gain':
        return multiplier * (b?.unrealizedGains - a?.unrealizedGains);
      case 'items':
        return multiplier * (b?.itemCount - a?.itemCount);
      default:
        return 0;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortButton = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-xs font-medium text-muted-foreground hover:text-card-foreground transition-colors duration-200"
    >
      <span>{label}</span>
      {sortBy === field && (
        <Icon 
          name={sortOrder === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
          size={14} 
        />
      )}
    </button>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-1">Collection Comparison</h3>
          <p className="text-sm text-muted-foreground">Performance metrics across collections</p>
        </div>
        <Icon name="BarChart3" size={20} className="text-primary" />
      </div>

      {sortedData?.length > 0 ? (
        <div className="space-y-4">
          {/* Table Header */}
          <div className="grid grid-cols-6 gap-4 pb-3 border-b border-border">
            <div className="col-span-2">
              <span className="text-xs font-medium text-muted-foreground">Collection</span>
            </div>
            <div className="text-right">
              <SortButton field="items" label="Items" />
            </div>
            <div className="text-right">
              <SortButton field="value" label="Value" />
            </div>
            <div className="text-right">
              <SortButton field="roi" label="ROI" />
            </div>
            <div className="text-right">
              <SortButton field="gain" label="Gain" />
            </div>
          </div>

          {/* Table Rows */}
          {sortedData?.map((collection) => (
            <div 
              key={collection?.id} 
              className="grid grid-cols-6 gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200"
            >
              <div className="col-span-2 flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={18} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-card-foreground truncate">
                    {collection?.name}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {collection?.type?.replace('-', ' ')}
                  </p>
                </div>
              </div>
              
              <div className="text-right flex items-center justify-end">
                <span className="text-sm text-card-foreground">
                  {collection?.itemCount}
                </span>
              </div>
              
              <div className="text-right flex items-center justify-end">
                <span className="text-sm font-medium text-card-foreground">
                  ${collection?.totalValue?.toLocaleString()}
                </span>
              </div>
              
              <div className="text-right flex items-center justify-end">
                <div className={`flex items-center space-x-1 ${
                  collection?.roi >= 0 ? 'text-success' : 'text-destructive'
                }`}>
                  <Icon 
                    name={collection?.roi >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                    size={14} 
                  />
                  <span className="text-sm font-medium">
                    {collection?.roi >= 0 ? '+' : ''}{collection?.roi?.toFixed(2)}%
                  </span>
                </div>
              </div>
              
              <div className="text-right flex items-center justify-end">
                <span className={`text-sm font-medium ${
                  collection?.unrealizedGains >= 0 ? 'text-success' : 'text-destructive'
                }`}>
                  {collection?.unrealizedGains >= 0 ? '+' : ''}${Math.abs(collection?.unrealizedGains)?.toLocaleString()}
                </span>
              </div>
            </div>
          ))}

          {/* Summary Row */}
          <div className="grid grid-cols-6 gap-4 pt-4 border-t border-border">
            <div className="col-span-2 flex items-center">
              <span className="text-sm font-semibold text-card-foreground">Total</span>
            </div>
            <div className="text-right flex items-center justify-end">
              <span className="text-sm font-semibold text-card-foreground">
                {sortedData?.reduce((sum, col) => sum + col?.itemCount, 0)}
              </span>
            </div>
            <div className="text-right flex items-center justify-end">
              <span className="text-sm font-semibold text-card-foreground">
                ${sortedData?.reduce((sum, col) => sum + col?.totalValue, 0)?.toLocaleString()}
              </span>
            </div>
            <div className="text-right flex items-center justify-end">
              <span className="text-sm font-semibold text-primary">
                {(() => {
                  const totalCost = sortedData?.reduce((sum, col) => sum + col?.totalCost, 0);
                  const totalValue = sortedData?.reduce((sum, col) => sum + col?.totalValue, 0);
                  const avgROI = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
                  return `${avgROI >= 0 ? '+' : ''}${avgROI?.toFixed(2)}%`;
                })()}
              </span>
            </div>
            <div className="text-right flex items-center justify-end">
              <span className="text-sm font-semibold text-success">
                +${sortedData?.reduce((sum, col) => sum + col?.unrealizedGains, 0)?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">No collections to compare</p>
          <p className="text-sm text-muted-foreground">Create collections to see performance comparisons</p>
        </div>
      )}
    </div>
  );
};

export default CollectionComparison;